import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, Column, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'

import { BaseComponent, Bid, FeatureType, Grid, ModalBackDrop, ModalSize, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { BidComponent } from './bid.component'
import { CompetitorBidsComponent } from './competitor-bids.component'

@Component({
    templateUrl: './all-histories.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        WjInputModule,
    ],
})
export class AllHistoriesComponent extends BaseComponent {
    faSearch = faSearch
    active = 1
    grid: Grid<Bid>
    allItems: Bid[] = []
    jobTypeMap!: DataMap
    countiesMap!: DataMap
    ownersMap!: DataMap
    allUserMap!: DataMap
    activeEstimatorUserMap!: DataMap
    biddersMap!: DataMap
    rangeMap!: DataMap
    maxCompetitorBids = 0

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsEdit])
    }

    constructor() {
        super()
        this.grid = new Grid<any>(this.services, 'id', this)
            .wrapColumnHeaders()
            .filterBy(item => [item.heavyBidNum, item.estimator, item.owner, item.job, item.bidType])
            .setupModalEditing(this.services.data.bid, 'Bid', BidComponent, () => new Bid(), this, ModalSize.small)
            .isReadOnly(!this.canEdit)
            .editOnDoubleClick(false)

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.bid.queryAll(),
                    this.services.data.jobTypes.queryAll(),
                    this.services.data.counties.queryAll(),
                    this.services.data.get('/api/VpOwner/GetOwners'),
                    this.services.data.get('/api/VpOwner/GetCompetitors'),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.get('api/VpUser/GetVistaEstimatorUsers'),
                    this.services.data.bidRanges.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.allItems = result[0]
                        this.jobTypeMap = new DataMap(result[1], 'id', 'fullName')
                        this.countiesMap = new DataMap(result[2], 'id', 'combinedName')
                        this.ownersMap = new DataMap(result[3], 'id', 'name')
                        this.biddersMap = new DataMap(result[4], 'id', 'name')
                        this.rangeMap = new DataMap(result[7], 'id', 'name')

                        // We want the grid to show a value for any user, even inactive ones.
                        this.allUserMap = new DataMap(result[5] as VpUser[], 'id', 'name')

                        // When the grid is edited, we will only show active users as options.
                        this.activeEstimatorUserMap = new DataMap(result[6] as VpUser[], 'id', 'name')

                        // Calculate the maximum number of competitor bids across all bids
                        this.maxCompetitorBids = this.calculateMaxCompetitorBids()

                        // Add computed properties for competitor columns to enable proper export
                        this.addCompetitorProperties()

                        // Always show all items (no filtering)
                        this.filterItems()
                    },
                }),
        )
    }

    calculateMaxCompetitorBids(): number {
        if (!this.allItems || this.allItems.length === 0) {
            return 0
        }

        return Math.max(
            ...this.allItems.map(bid => (bid.competitorBids && bid.competitorBids.length > 0 ? bid.competitorBids.length : 0)),
            0,
        )
    }

    addCompetitorProperties() {
        // Add computed properties for each competitor bid to enable proper grid binding and export
        this.allItems.forEach(bid => {
            if (bid.competitorBids) {
                bid.competitorBids.forEach((competitorBid, index) => {
                    // Add properties for competitor name, amount, and won
                    ;(bid as any)[`competitorBid${index}Comp`] = this.getCompetitorName(bid, index)
                    ;(bid as any)[`competitorBid${index}Amt`] = competitorBid.bidAmount || null
                    ;(bid as any)[`competitorBid${index}Won`] = competitorBid.won ?? false
                })
            }
            // Fill in empty properties for missing competitor bids up to maxCompetitorBids
            for (let i = bid.competitorBids?.length || 0; i < this.maxCompetitorBids; i++) {
                ;(bid as any)[`competitorBid${i}Comp`] = ''
                ;(bid as any)[`competitorBid${i}Amt`] = null
                ;(bid as any)[`competitorBid${i}Won`] = null
            }
        })
    }

    filterItems() {
        // Always show all items (no filtering based on wasBid)
        this.grid.items = this.allItems
    }

    mainGridItemFormatter(panel: GridPanel, r, c, cell: HTMLDivElement) {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const rowItem = row.dataItem

            cell.classList.remove('green-cell')

            // Green when VanCon is marked as won in competitor bids (not derived rank)
            if (rowItem.competitorBids?.some(cb => cb.competitorId === -1 && cb.won === true)) {
                cell.classList.add('green-cell')
            }
        }
    }

    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column data map to just active users, so inactive users cannot be selected.
        const col = flexGrid.columns[args.col] as Column
        if (col.binding == 'estimatorHrRef') col.dataMap = this.activeEstimatorUserMap
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column mapping back to all users, so inactive user names still appear.
        const col = flexGrid.columns[args.col]
        if (col.binding == 'estimatorHrRef') col.dataMap = this.allUserMap

        if (args.cancel) return
        this.updateBids([flexGrid.rows[args.row].dataItem])
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        let row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (row; row <= row2; row++) {
            const dataItem = flexGrid.rows[row].dataItem
            itemIds.push(dataItem.id)
        }

        // Since the grid will sort and filter the pasted cells before the 'onCellPasted' even fires,
        // we need to keep a record of the items we are changing, so they wrong items don't get send to the server.
        flexGrid['controller'].itemIds = itemIds
    }

    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const itemsToSave = flexGrid.collectionView.items.filter(x => flexGrid['controller'].itemIds.indexOf(x.id) > -1)
        this.updateBids(itemsToSave)
        flexGrid['controller'].pastedRimnumbers = [] // clear immediately and not on promise fulfillment.
    }

    updateBids(items: Bid[]) {
        this.services.data.put('api/Bid/BulkUpdate', items).subscribe({
            error: () => {
                this.services.notification.error('The bid changes were not successfully saved')
            },
        })
    }

    openCompetitorBidsModal(bid: Bid) {
        const ref = this.services.modals.open(CompetitorBidsComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as CompetitorBidsComponent
        instance.bid = this.services.utility.copy(bid)
        instance.biddersMap = this.biddersMap

        // Add a placeholder for a 'Select a bidder' option, but only if it has not already been added.
        if (instance.biddersMap.collectionView.items.find(item => item.id === 0) === undefined) {
            instance.biddersMap.collectionView.items.unshift({ id: 0, name: 'Select a Bidder' })
        }

        ref.result.then(
            () => {
                // Refresh the grid to show updated data
                this.refresh()
            },
            () => {
                // User cancelled, do nothing
            },
        )
    }

    getBiddersCount(bid: Bid): string {
        if (bid.competitorBids && bid.competitorBids.length > 0) {
            return `${bid.competitorBids.length} ${bid.competitorBids.length === 1 ? 'Bid' : 'Bids'}`
        }
        return 'No Bids'
    }

    onCheckboxChange(event: Event): void {
        event.preventDefault() // Prevent default checkbox behavior
    }

    // Get array of competitor indices for generating columns
    getCompetitorIndices(): number[] {
        return Array.from({ length: this.maxCompetitorBids }, (_, i) => i)
    }

    // Get competitor name for a specific bid index
    getCompetitorName(bid: Bid, index: number): string {
        if (bid.competitorBids && bid.competitorBids.length > index && bid.competitorBids[index]) {
            const competitorId = bid.competitorBids[index].competitorId
            if (this.biddersMap && competitorId) {
                const competitor = this.biddersMap.collectionView.items.find((item: any) => item.id === competitorId)
                return competitor ? competitor.name : ''
            }
        }
        return ''
    }

    // Get competitor ID for a specific bid index (used for DataMap binding)
    getCompetitorId(bid: Bid, index: number): number | null {
        if (bid.competitorBids && bid.competitorBids.length > index && bid.competitorBids[index]) {
            return bid.competitorBids[index].competitorId
        }
        return null
    }

    // Get competitor bid amount for a specific bid index
    getCompetitorAmount(bid: Bid, index: number): number | null {
        if (bid.competitorBids && bid.competitorBids.length > index && bid.competitorBids[index]) {
            return bid.competitorBids[index].bidAmount || null
        }
        return null
    }

    // Get competitor won flag for a specific bid index
    getCompetitorWon(bid: Bid, index: number): boolean | null {
        if (bid.competitorBids && bid.competitorBids.length > index && bid.competitorBids[index]) {
            return bid.competitorBids[index].won ?? false
        }
        return null
    }
}
